import React from "react";
import { Reveal } from "../components/Reveal";
import { AWWatermark } from "../components/AWWatermark";
import { useLanguage } from "../i18n/LanguageContext";
import { usePageMeta } from "../hooks/usePageMeta";

const About = () => {
  const { t, images } = useLanguage();
  usePageMeta(t.meta.about);

  return (
    <div data-testid="about-page" className="bg-white">
      {/* Page header */}
      <section className="pt-40 md:pt-48 pb-16 md:pb-24 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-6">
              — {t.about.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-aw text-5xl md:text-6xl lg:text-7xl font-light tracking-tight max-w-4xl leading-[1.05]">
              {t.about.heading}
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Image + Text */}
      <section className="px-6 md:px-12 pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <Reveal className="md:col-span-7" delay={0.1}>
            <img
              src={images.atelier}
              loading="lazy"
              alt="Atelier"
              className="w-full h-[560px] md:h-[720px] object-cover aw-grayscale"
            />
          </Reveal>
          <div className="md:col-span-5 md:pt-16">
            <Reveal delay={0.2}>
              <p className="font-sans-aw text-base md:text-lg text-black/75 leading-relaxed mb-8">
                {t.about.p1}
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <p className="font-sans-aw text-base md:text-lg text-black/75 leading-relaxed">
                {t.about.p2}
              </p>
            </Reveal>
            {t.about.services_list && (
              <Reveal delay={0.45}>
                <div className="mt-10 border-t border-black/15 pt-6">
                  <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-4">{t.about.services_title}</p>
                  <ul className="font-sans-aw text-sm md:text-base text-black/75 space-y-2 leading-relaxed">
                    {t.about.services_list.map((s) => (
                      <li key={s} className="flex gap-3"><span className="text-black/30">—</span><span>{s}</span></li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="values-section" className="relative bg-[#fafafa] py-28 md:py-40 px-6 md:px-12 overflow-hidden">
        <AWWatermark size={520} opacity={0.04} position="left" />
        <div className="relative max-w-[1400px] mx-auto">
          <Reveal>
            <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-6">
              — {t.about.values_title}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif-aw text-3xl md:text-5xl font-light tracking-tight mb-20 max-w-3xl">
              {t.closing.values_heading}
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-12 md:gap-16">
            {t.about.values.map((value, idx) => (
              <Reveal key={value.title} delay={0.2 + idx * 0.12}>
                <div className="border-t border-black pt-8">
                  <span className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/40">
                    0{idx + 1}
                  </span>
                  <h3 className="font-serif-aw text-2xl md:text-3xl mt-4 mb-5 font-light">
                    {value.title}
                  </h3>
                  <p className="font-sans-aw text-sm md:text-base text-black/65 leading-relaxed">
                    {value.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
